import { useEffect, useRef } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import knowledgeHero from '@/assets/knowledge-hero.jpg';
import { judgementsData, statisticsData } from '@/mocks/knowledgeData';
import biharEthanolPolicy2021 from '@/assets/Bihar Ethanol Policy 2021.pdf';
import biharBiofuelsPolicy2023 from '@/assets/Bihar Biofuels Policy 2023.pdf';
import biharExciseSop from '@/assets/Bihar Excise Ethanol Industrial Use SOP.pdf';
import csBiharProceeding from '@/assets/CS Bihar Proceeding Ethanol Molasses Biodiesel.pdf';
import biharExciseDenaturant from '@/assets/Bihar Excise Old Denaturant Notification.pdf';
import nitiAayogPolicy from '@/assets/Niti Aayog Ethanol Policy.pdf';
import completeProposals from '@/assets/Complete list of Proposals approved under various Ethanol IS Schemes.pdf';
import singleWindowSystem from '@/assets/Single window system for approvals of Ethanol Projects.pdf';
import impactAssessment from '@/assets/Impact assessment of interest subvention scheme.pdf';
import schemeForExtending6 from '@/assets/Scheme for extending financial assistance for sugar mills - 6.pdf';
import supplyUnderEbp from '@/assets/Supply under Ethanol Blended with Petrol (EBP) Programme.pdf';
import biharExciseDenaturantNew from '@/assets/Bihar Excise Denautant Notification.pdf';
import biharExciseLoi from '@/assets/Bihar Excise LOI Licence Guidelines.pdf';
import productionFromSugar from '@/assets/Production of Ethanol from sugar or molasses based distilleries.pdf';
import newSchemeSugarMills from '@/assets/New Scheme for Sugar Mills.pdf';
import listProposalsIs from '@/assets/List of proposals approved ethanol Interest subvention schemes.pdf';
import schemeForExtending1 from '@/assets/Scheme for extending financial assistance for sugar mills - 1.pdf';
import schemeForExtending5 from '@/assets/Scheme for extending financial assistance for sugar mills - 5.pdf';

const useScrollReveal = (ref: React.RefObject<HTMLDivElement | null>) => {
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.06 }
    );
    ref.current?.querySelectorAll('.animate-on-scroll').forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
};

const KnowledgeHub = () => {
  const pageRef = useRef<HTMLDivElement>(null);
  useScrollReveal(pageRef);

  return (
    <div className="font-jakarta bg-white">
      <Navbar />
      <main ref={pageRef}>

        {/* Hero */}
        <section className="pt-16 bg-white">
          <div className="max-w-4xl mx-auto px-6 md:px-10 pt-12 pb-10 text-center">
            <div className="animate-on-scroll mb-4">
              <span className="text-[11px] font-bold text-bteal tracking-widest uppercase">Knowledge Hub</span>
            </div>
            <h1 className="animate-on-scroll text-4xl md:text-5xl font-bold text-ink leading-tight tracking-tight mb-4">
              Policies, Research &amp; Reports
            </h1>
            <p className="animate-on-scroll text-ink-muted text-lg max-w-xl mx-auto leading-relaxed">
              Your comprehensive resource for ethanol policy updates, industry research, whitepapers, and BEA&apos;s annual reports.
            </p>
          </div>
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="animate-on-scroll rounded-lg overflow-hidden w-full" style={{ height: '300px' }}>
              <img
                src={knowledgeHero}
                alt="Knowledge Hub"
                className="w-full h-full object-cover object-center"
              />
            </div>
          </div>
          <div className="h-px bg-gray-100 mt-0" />
        </section>

        {/* ── POLICIES & UPDATES ── */}
        <section id="policies" className="py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="flex items-end justify-between gap-4 mb-12 flex-wrap">
              <div>
                <span className="text-[11px] font-bold text-bgreen tracking-widest uppercase">Regulatory Framework</span>
                <h2 className="animate-on-scroll text-3xl md:text-4xl font-bold text-ink mt-2 tracking-tight">Policies &amp; Updates</h2>
                <p className="text-ink-muted text-sm mt-2 max-w-lg">Key policies governing Bihar&apos;s ethanol sector at state and central levels.</p>
              </div>
            </div>
            <div className="space-y-10">
              {[
                {
                  title: "Ministry of Petroleum and Natural Gas, Government of India",
                  authority: "Central Government",
                  documents: [
                    { name: "Niti Aayog Ethanol Policy", type: "pdf", url: nitiAayogPolicy }
                  ]
                },
                {
                  title: "Department of Food and Public Distribution, Government of India",
                  authority: "Central Government",
                  documents: [
                    { name: "Complete list of Proposals approved under various Ethanol IS Schemes", type: "pdf", url: completeProposals },
                    { name: "Production of Ethanol from sugar or molasses based distilleries", type: "pdf", url: productionFromSugar },
                    { name: "New Scheme for Sugar", type: "pdf", url: newSchemeSugarMills },
                    { name: "Single window system for approvals of Ethanol Projects", type: "pdf", url: singleWindowSystem },
                    { name: "Impact assessment of interest subvention scheme", type: "pdf", url: impactAssessment },
                    { name: "List of proposals approved ethanol Interest subvention schemes", type: "pdf", url: listProposalsIs },
                    { name: "Scheme for extending financial assistance for sugar mills-1", type: "pdf", url: schemeForExtending1 },
                    { name: "Scheme for extending financial assistance for sugar mills-5", type: "pdf", url: schemeForExtending5 },
                    { name: "Scheme for extending financial assistance for sugar mills-6", type: "pdf", url: schemeForExtending6 },
                    { name: "Supply under Ethanol Blended with Petrol (EBP) Programme", type: "pdf", url: supplyUnderEbp }
                  ]
                },
                {
                  title: "National Consumer Cooperative Federation of India Limited / National Agricultural Cooperative Marketing Federation of India Ltd / Food Corporation of India",
                  authority: "Central Agencies",
                  documents: []
                },
                {
                  title: "Excise Department, Government of Bihar",
                  authority: "State Government",
                  documents: [
                    { name: "Bihar Excise Ethanol Industrial Use SOP", type: "pdf", url: biharExciseSop },
                    { name: "CS Bihar Proceeding Ethanol Molasses Biodiesel", type: "pdf", url: csBiharProceeding },
                    { name: "Bihar Excise Old Denaturant Notification", type: "pdf", url: biharExciseDenaturant },
                    { name: "Bihar Excise Denaturant Notification", type: "pdf", url: biharExciseDenaturantNew },
                    { name: "Bihar Excise LOI Licence Guidelines", type: "pdf", url: biharExciseLoi }
                  ]
                },
                {
                  title: "Industries Department, Government of Bihar",
                  authority: "State Government",
                  documents: [
                    { name: "Bihar Ethanol Policy 2021", type: "pdf", url: biharEthanolPolicy2021 },
                    { name: "Bihar Biofuels Policy 2023", type: "pdf", url: biharBiofuelsPolicy2023 }
                  ]
                }
              ].map((section, i) => {
                const isEven = i % 2 === 0;
                return (
                  <div
                    key={i}
                    className={`animate-on-scroll rounded-2xl p-6 md:p-8 transition-all duration-200 border ${
                      isEven
                        ? 'bg-surface border-gray-200/90 shadow-sm'
                        : 'bg-emerald-900/[0.035] border-bteal/20 shadow-sm'
                    }`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-5 mb-6 border-b border-gray-200/70">
                      <div className="flex items-start gap-3">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${
                          isEven ? 'bg-bteal/10 text-bteal' : 'bg-bgreen/15 text-bgreen-dark'
                        }`}>
                          <i className="ri-government-line text-lg" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded ${
                              section.authority === "State Government"
                                ? "bg-amber-100/80 text-amber-900"
                                : "bg-blue-50 text-blue-800"
                            }`}>
                              {section.authority}
                            </span>
                          </div>
                          <h3 className="text-lg md:text-xl font-bold text-ink leading-snug">{section.title}</h3>
                        </div>
                      </div>
                      <div className="flex-shrink-0 self-start sm:self-auto">
                        <span className={`text-xs font-semibold px-3 py-1 rounded-full ${
                          section.documents.length > 0 
                            ? 'bg-bteal/10 text-bteal' 
                            : 'bg-gray-100 text-gray-500'
                        }`}>
                          {section.documents.length} {section.documents.length === 1 ? 'Document' : 'Documents'}
                        </span>
                      </div>
                    </div>

                    {section.documents.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {section.documents.map((doc, j) => (
                          <div
                            key={j}
                            className="rounded-xl border p-4 transition-all duration-200 flex items-center justify-between gap-3 bg-white border-gray-100 hover:border-bteal/30 hover:shadow-md"
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-red-50 text-red-500 flex-shrink-0">
                                <i className="ri-file-pdf-2-line text-xl" />
                              </div>
                              <h4 className="font-semibold text-ink text-sm leading-snug truncate" title={doc.name}>{doc.name}</h4>
                            </div>
                            <button
                              onClick={() => doc.url && doc.url !== "#" ? window.open(doc.url, '_blank') : alert('Document coming soon')}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-bteal text-bteal text-xs font-semibold hover:bg-bteal hover:text-white transition-all duration-150 cursor-pointer whitespace-nowrap flex-shrink-0"
                            >
                              <i className="ri-download-line text-xs" />
                              <span className="hidden sm:inline">Download</span>
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 p-4 rounded-xl border border-dashed border-gray-200 bg-white/70 text-ink-muted text-xs">
                        <i className="ri-folder-info-line text-base text-gray-400" />
                        <span>No policy notifications or documents currently published under this body.</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── JUDGEMENTS & CASE STUDIES ── */}
        <section id="judgements" className="py-16 md:py-24 bg-surface border-t border-gray-100">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="mb-12">
              <span className="text-[11px] font-bold text-bteal tracking-widest uppercase">Legal Precedents</span>
              <h2 className="animate-on-scroll text-3xl md:text-4xl font-bold text-ink mt-2 tracking-tight">Judgements &amp; Case Studies</h2>
              <p className="text-ink-muted text-sm mt-2 max-w-xl">
                Key Supreme Court rulings, High Court orders, and legal precedents shaping India&apos;s biofuel and industrial alcohol regulatory landscape.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {judgementsData.map((doc) => (
                <div
                  key={doc.srNo}
                  className="animate-on-scroll bg-white rounded-xl p-4 border border-gray-200/90 shadow-2xs hover:border-bteal/30 hover:shadow-md transition-all duration-200 flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-blue-50 text-blue-800">
                        {doc.category || 'Judgement'}
                      </span>
                      <span className="text-xs font-semibold text-ink-muted flex items-center gap-1">
                        <i className="ri-calendar-line text-gray-400" />
                        {doc.date}
                      </span>
                    </div>

                    <div className="flex items-start gap-3 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-red-50 text-red-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-scales-3-line text-lg" />
                      </div>
                      <div>
                        <h3 className="font-bold text-ink text-sm sm:text-base leading-snug hover:text-bteal transition-colors">
                          {doc.title}
                        </h3>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-end mt-1">
                    <a
                      href={doc.pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-bteal text-white text-xs font-semibold hover:bg-bteal-dark transition-colors shadow-2xs"
                    >
                      <i className="ri-download-line text-xs" />
                      <span>Download PDF</span>
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── STATISTICS & GENERAL DATA ── */}
        <section id="statistics" className="py-16 md:py-24 bg-white border-t border-gray-100">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="mb-12">
              <span className="text-[11px] font-bold text-bgreen tracking-widest uppercase">Industry Data &amp; Pricing</span>
              <h2 className="animate-on-scroll text-3xl md:text-4xl font-bold text-ink mt-2 tracking-tight">Statistics &amp; General Data</h2>
              <p className="text-ink-muted text-sm mt-2 max-w-xl">
                Ethanol supply statistics, feedstock price trends, export data, and agricultural cultivation metrics for grain-based ethanol.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {statisticsData.map((doc) => (
                <div
                  key={doc.srNo}
                  className="animate-on-scroll bg-surface rounded-xl p-4 border border-gray-200/90 shadow-2xs hover:border-bteal/30 hover:bg-white hover:shadow-md transition-all duration-200 flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200/60">
                        {doc.category || 'Data Report'}
                      </span>
                      <span className="text-xs font-semibold text-ink-muted flex items-center gap-1">
                        <i className="ri-calendar-line text-gray-400" />
                        {doc.date}
                      </span>
                    </div>

                    <div className="flex items-start gap-3 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-bteal/10 text-bteal flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-bar-chart-2-line text-lg" />
                      </div>
                      <div>
                        <h3 className="font-bold text-ink text-sm sm:text-base leading-snug hover:text-bteal transition-colors">
                          {doc.title}
                        </h3>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-end mt-1">
                    <a
                      href={doc.pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-bteal text-bteal text-xs font-semibold hover:bg-bteal hover:text-white transition-all cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-download-line text-xs" />
                      <span>Download PDF</span>
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
};

export default KnowledgeHub;
